import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';
import PublicFooter from '../components/layout/PublicFooter';
import { API_URL, ACH_PRICE } from "../config";
import Header from './component/Header';
import Modal from 'react-modal';
import PaymentForm from './component/PaymentGateway';
import Swal from 'sweetalert2';
import Loading from './component/Loading';
import Switch from 'react-switch';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Link } from 'react-router-dom';

const PaymentMethods = () => {
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('ach');
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [transitTokenObject, setTransitTokenObject] = useState(null);
    const [accountNumber, setAccountNumber] = useState('');
    const [repeatAccountNumber, setRepeatAccountNumber] = useState('');
    const [routingNumber, setRoutingNumber] = useState('');
    const [accountType, setAccountType] = useState('');
    const [cardType, setCardType] = useState('');
    const [bankInfo, setBankInfo] = useState(null);
    const [accountName, setAccountName] = useState('');
    const [address, setAddress] = useState('');
    const [city, setCity] = useState('');
    const [achAgree, setAchAgree] = useState(false);
    const [creditCardAgree, setCreditCardAgree] = useState(false);
    const [termsAgree, setTermsAgree] = useState(false);
    const [smsTermsAgree, setSmsTermsAgree] = useState(false);
    const [serviceFee, setServiceFee] = useState(ACH_PRICE);
    const [updateExistingRenewals, setUpdateExistingRenewals] = useState(false);

    const maskAccountNumber = (fullAccountNumber) => {
      if (fullAccountNumber && fullAccountNumber.length >= 4) {
        const last4Digits = fullAccountNumber.slice(-4);
        return `••••${last4Digits}`;
      }
      return fullAccountNumber;
    };

    const loadPaymentMethods = () => {
        const authToken = localStorage.getItem('token');
        if (!authToken) return;

        fetch(`${API_URL}/user/payment-methods`, {
          method: 'GET',
          headers: { Authorization: `Bearer ${authToken}` },
        })
          .then((response) => {
            if (!response.ok) throw new Error('Network response was not ok');
            return response.json();
          })
          .then((data) => {
            setPaymentMethods(data);
            setLoading(false);
          })
          .catch((error) => {
            console.error('Error fetching payment methods:', error);
            setLoading(false);
          });
    };

    useEffect(() => { loadPaymentMethods(); }, []);

    const filteredPaymentMethods = paymentMethods.filter(
        (method) =>
            (activeTab === 'ach' && method.payment_method.toLowerCase() === 'ach') ||
            (activeTab === 'creditCard' && method.payment_method.toLowerCase() === 'creditcard')
    );

    const handleAddMoreClick = () => setIsAddModalOpen(true);

    const handleAddPaymentMethod = () => {
        if (!isFormValid()) return;
        setLoading(true);
        const authToken = localStorage.getItem('token');
        if (!authToken) return;

        fetch(API_URL + '/user/payment-methods/save', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            activeTab, transitTokenObject, accountNumber, repeatAccountNumber,
            routingNumber, accountType, cardType, accountName, address, city,
            update_existing_renewals: updateExistingRenewals
          }),
        })
          .then((response) => {
            setLoading(false);
            if (response.status === 422) {
              return response.json().then((data) => {
                const errorMessages = Object.values(data.errors).join('\n');
                throw new Error(errorMessages);
              });
            } else if (!response.ok) {
              throw new Error('Network response was not ok');
            }
            return response.json();
          })
          .then((data) => {
            loadPaymentMethods();
            setIsAddModalOpen(false);
            setTransitTokenObject(null);
            setAccountNumber('');
            setRepeatAccountNumber('');
            setRoutingNumber('');
            setAccountType('');
            setCardType('');
            setBankInfo(null);
            setAddress('');
            setCity('');
            setUpdateExistingRenewals(false);
            Swal.fire({ icon: 'success', title: 'Success!', text: "Payment method has been successfully created.", showConfirmButton: true });
          })
          .catch((error) => {
            Swal.fire({ icon: 'error', title: 'Error submitting the form', text: error, showCloseButton: true, showConfirmButton: true });
          });
    };

    const isFormValid = () => {
        const newErrors = { paymentMethod: '' };
        let isValid = true;

        if (!activeTab) { newErrors.paymentMethod = 'Payment method is required.'; isValid = false; }

        if (activeTab === 'ach') {
          if (!accountNumber) { newErrors.accountNumber = 'Account number is required.'; isValid = false; }
          if (!repeatAccountNumber) { newErrors.repeatAccountNumber = 'Repeat Account number is required.'; isValid = false; }
          if (accountNumber !== repeatAccountNumber) { newErrors.repeatAccountNumber = 'Account number & Repeat Account number must be same.'; isValid = false; }
          if (!accountType) { newErrors.accountType = 'Account type is required.'; isValid = false; }
          if (!routingNumber) { newErrors.routingNumber = 'Routing number is required.'; isValid = false; }
          else if (!/^\d{9}$/.test(routingNumber)) { newErrors.routingNumber = 'Invalid routing number format. It should be 9 digits.'; isValid = false; }
        } else {
          if (!transitTokenObject) { newErrors.accountType = 'Please provide valid card information'; isValid = false; }
        }

        if ((activeTab === "ach" && !achAgree) || (activeTab === "creditCard" && !creditCardAgree)) {
          newErrors.termsAgree = 'Please check All terms checkboxes.'; isValid = false;
        }

        const combinedErrorMessage = Object.values(newErrors).filter((message) => message).join('\n');
        if (!isValid) {
          Swal.fire({ icon: 'error', title: 'Please fix the following issues:', html: combinedErrorMessage.replace(/\n/g, '<br>'), showCloseButton: true, showConfirmButton: true });
        }
        return isValid;
    };

    const toggleIsDefault = (id, isDefault) => {
        const authToken = localStorage.getItem('token');
        if (!authToken) return;

        const updatedMethods = paymentMethods.map((method) => {
            if (method.id === id) method.is_default = isDefault;
            return method;
        });
        setPaymentMethods(updatedMethods);

        const newStatusMessage = isDefault ? 'Payment method set as default.' : 'Payment method removed as default.';

        fetch(`${API_URL}/user/payment-methods/update-is-default/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
            body: JSON.stringify({ is_default: isDefault }),
        })
          .then((response) => {
            if (!response.ok) toast.error('Network response was not ok', { autoClose: 3000 });
            loadPaymentMethods();
            toast.success(newStatusMessage, { autoClose: 3000 });
          })
          .catch((error) => { toast.error('Error updating is_default', { autoClose: 3000 }); });
    };

    const toggleEnabled = (id, isEnabled) => {
        const authToken = localStorage.getItem('token');
        if (!authToken) return;

        const updatedMethods = paymentMethods.map((method) => {
          if (method.id === id) method.enabled = isEnabled;
          return method;
        });
        setPaymentMethods(updatedMethods);

        const newStatusMessage = isEnabled ? 'Payment method activated.' : 'Payment method deactivated.';

        fetch(`${API_URL}/user/payment-methods/update-enabled/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
            body: JSON.stringify({ enabled: isEnabled }),
        })
          .then((response) => {
            if (!response.ok) toast.error('Network response was not ok', { autoClose: 3000 });
            toast.success(newStatusMessage, { autoClose: 3000 });
          })
          .catch((error) => { toast.error('Error updating enabled', { autoClose: 3000 }); });
    };

    const switchProps = { onColor: "#16a34a", offColor: "#d1d5db", width: 44, height: 24, handleDiameter: 20, uncheckedIcon: false, checkedIcon: false };

    return (
        <div className="tw-min-h-screen tw-flex tw-flex-col tw-bg-gray-50">
            <Header />
            {loading && <Loading text="Please wait..." />}

            <div className="tw-flex-1 tw-max-w-7xl tw-mx-auto tw-w-full tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-py-8">
                {/* Page header */}
                <div className="tw-mb-8">
                  <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-500 tw-mb-2">
                    <Link to="/account" className="tw-text-blue-600 hover:tw-text-blue-700 tw-no-underline">Dashboard</Link>
                    <svg className="tw-w-4 tw-h-4 tw-text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                    <span>Payment Methods</span>
                  </div>
                  <h1 className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-m-0">Payment Methods</h1>
                </div>

                {/* Tabs + Add button */}
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-6">
                    <div className="tw-flex tw-gap-1 tw-bg-gray-200 tw-rounded-lg tw-p-1">
                        <button
                            onClick={() => setActiveTab('ach')}
                            className={`tw-px-5 tw-py-2 tw-text-sm tw-font-medium tw-rounded-md tw-border-0 tw-cursor-pointer tw-transition-all ${
                                activeTab === 'ach'
                                    ? 'tw-bg-white tw-text-gray-900 tw-shadow-sm'
                                    : 'tw-bg-transparent tw-text-gray-600 hover:tw-text-gray-900'
                            }`}
                        >
                            ACH / eCheck
                        </button>
                        <button
                            onClick={() => setActiveTab('creditCard')}
                            className={`tw-px-5 tw-py-2 tw-text-sm tw-font-medium tw-rounded-md tw-border-0 tw-cursor-pointer tw-transition-all ${
                                activeTab === 'creditCard'
                                    ? 'tw-bg-white tw-text-gray-900 tw-shadow-sm'
                                    : 'tw-bg-transparent tw-text-gray-600 hover:tw-text-gray-900'
                            }`}
                        >
                            Credit / Debit Card
                        </button>
                    </div>
                    <button
                        onClick={handleAddMoreClick}
                        className="tw-inline-flex tw-items-center tw-gap-2 tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white tw-text-sm tw-font-medium tw-px-4 tw-py-2 tw-rounded-lg tw-border-0 tw-cursor-pointer tw-transition-colors"
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        Add Payment Method
                    </button>
                </div>

                {/* Table */}
                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-shadow-sm">
                    <div className="tw-overflow-x-auto">
                        <table className="tw-w-full">
                            <thead>
                                <tr className="tw-border-b tw-border-gray-200 tw-bg-gray-50">
                                    <th className="tw-text-left tw-px-6 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Created</th>
                                    {activeTab === 'creditCard' && (
                                        <>
                                            <th className="tw-text-left tw-px-6 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Payment ID</th>
                                            <th className="tw-text-left tw-px-6 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Card Holder</th>
                                            <th className="tw-text-left tw-px-6 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Card Number</th>
                                            <th className="tw-text-left tw-px-6 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Type</th>
                                            <th className="tw-text-left tw-px-6 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Expires</th>
                                        </>
                                    )}
                                    {activeTab === 'ach' && (
                                        <>
                                            <th className="tw-text-left tw-px-6 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Account Number</th>
                                            <th className="tw-text-left tw-px-6 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Routing Number</th>
                                            <th className="tw-text-left tw-px-6 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Account Type</th>
                                        </>
                                    )}
                                    <th className="tw-text-center tw-px-6 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Default</th>
                                    <th className="tw-text-center tw-px-6 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Enabled</th>
                                </tr>
                            </thead>
                            <tbody className="tw-divide-y tw-divide-gray-100">
                                {loading ? (
                                    <tr>
                                        <td colSpan="12" className="tw-px-6 tw-py-12 tw-text-center tw-text-sm tw-text-gray-500">
                                            <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
                                                <svg className="tw-animate-spin tw-w-5 tw-h-5 tw-text-blue-500" fill="none" viewBox="0 0 24 24">
                                                    <circle className="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                                </svg>
                                                Loading...
                                            </div>
                                        </td>
                                    </tr>
                                ) : filteredPaymentMethods.length === 0 ? (
                                    <tr>
                                        <td colSpan="12" className="tw-px-6 tw-py-12 tw-text-center">
                                            <div className="tw-flex tw-flex-col tw-items-center tw-gap-3">
                                                <div className="tw-w-12 tw-h-12 tw-rounded-full tw-bg-gray-100 tw-flex tw-items-center tw-justify-center">
                                                    <svg className="tw-w-6 tw-h-6 tw-text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15A2.25 2.25 0 002.25 6.75v10.5A2.25 2.25 0 004.5 19.5z" /></svg>
                                                </div>
                                                <p className="tw-text-sm tw-text-gray-500 tw-m-0">No {activeTab === 'ach' ? 'ACH' : 'credit card'} payment methods</p>
                                                <button onClick={handleAddMoreClick} className="tw-text-sm tw-text-blue-600 hover:tw-text-blue-700 tw-font-medium tw-border-0 tw-bg-transparent tw-cursor-pointer">
                                                    Add one now
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    filteredPaymentMethods.map((method, index) => (
                                        <tr key={index} className="hover:tw-bg-gray-50 tw-transition-colors">
                                            <td className="tw-px-6 tw-py-4 tw-text-sm tw-text-gray-700">
                                                {method.created_at ? new Date(method.created_at).toLocaleDateString() : '-'}
                                            </td>
                                            {activeTab === 'creditCard' ? (
                                                <>
                                                    <td className="tw-px-6 tw-py-4 tw-text-sm tw-font-mono tw-text-gray-600 tw-text-xs">{method.pay_payment_id || '-'}</td>
                                                    <td className="tw-px-6 tw-py-4 tw-text-sm tw-text-gray-700">{method.card_holder_name || '-'}</td>
                                                    <td className="tw-px-6 tw-py-4 tw-text-sm tw-font-mono tw-text-gray-700">{maskAccountNumber(method.masked_card_number) || '-'}</td>
                                                    <td className="tw-px-6 tw-py-4 tw-text-sm tw-text-gray-700">{method.card_type || '-'}</td>
                                                    <td className="tw-px-6 tw-py-4 tw-text-sm tw-text-gray-700">{method.expiration_date || '-'}</td>
                                                </>
                                            ) : (
                                                <>
                                                    <td className="tw-px-6 tw-py-4 tw-text-sm tw-font-mono tw-text-gray-700">{maskAccountNumber(method.account_number) || '-'}</td>
                                                    <td className="tw-px-6 tw-py-4 tw-text-sm tw-font-mono tw-text-gray-700">{method.routing_number || '-'}</td>
                                                    <td className="tw-px-6 tw-py-4 tw-text-sm tw-text-gray-700">{method.account_type || '-'}</td>
                                                </>
                                            )}
                                            <td className="tw-px-6 tw-py-4 tw-text-center">
                                                <Switch onChange={() => toggleIsDefault(method.id, !method.is_default)} checked={Boolean(method.is_default)} {...switchProps} />
                                            </td>
                                            <td className="tw-px-6 tw-py-4 tw-text-center">
                                                <Switch onChange={() => toggleEnabled(method.id, !method.enabled)} checked={Boolean(method.enabled)} {...switchProps} />
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <ToastContainer />
            <PublicFooter />

            {/* Add Payment Method Modal */}
            <Modal
                isOpen={isAddModalOpen}
                onRequestClose={() => setIsAddModalOpen(false)}
                className="tw-bg-white tw-rounded-xl tw-shadow-2xl tw-max-w-2xl tw-w-full tw-mx-4 tw-max-h-[90vh] tw-overflow-y-auto tw-p-6 tw-outline-none"
                overlayClassName="tw-fixed tw-inset-0 tw-bg-black/50 tw-flex tw-items-center tw-justify-center tw-z-[9999]"
            >
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-6">
                    <h2 className="tw-text-xl tw-font-bold tw-text-gray-900 tw-m-0">Add Payment Method</h2>
                    <button onClick={() => setIsAddModalOpen(false)} className="tw-text-gray-400 hover:tw-text-gray-600 tw-border-0 tw-bg-transparent tw-cursor-pointer tw-p-1">
                        <FontAwesomeIcon icon={faTimes} className="tw-text-lg" />
                    </button>
                </div>
                <PaymentForm
                    accountType={accountType} setAccountType={setAccountType}
                    cardType={cardType} setCardType={setCardType}
                    routingNumber={routingNumber} setRoutingNumber={setRoutingNumber}
                    accountNumber={accountNumber} setAccountNumber={setAccountNumber}
                    setRepeatAccountNumber={setRepeatAccountNumber}
                    paymentMethod={activeTab} setPaymentMethod={setActiveTab}
                    transitTokenObject={transitTokenObject} setTransitTokenObject={setTransitTokenObject}
                    bankInfo={bankInfo} setBankInfo={setBankInfo}
                    accountName={accountName} setAccountName={setAccountName}
                    address={address} setAddress={setAddress}
                    city={city} setCity={setCity}
                    isShowTermsAndConditions={false}
                    achAgree={achAgree} setAchAgree={setAchAgree}
                    creditCardAgree={creditCardAgree} setCreditCardAgree={setCreditCardAgree}
                    termsAgree={termsAgree} setTermsAgree={setTermsAgree}
                    smsTermsAgree={smsTermsAgree} setSmsTermsAgree={setSmsTermsAgree}
                    serviceFee={serviceFee} setServiceFee={setServiceFee}
                />
                <div className="tw-mt-6 tw-pt-4 tw-border-t tw-border-gray-200">
                    <label className="tw-flex tw-items-start tw-gap-3 tw-cursor-pointer tw-mb-4">
                        <input
                            type="checkbox"
                            checked={updateExistingRenewals}
                            onChange={(e) => setUpdateExistingRenewals(e.target.checked)}
                            className="tw-mt-0.5 tw-w-4 tw-h-4 tw-rounded tw-border-gray-300 tw-text-blue-600 tw-cursor-pointer"
                        />
                        <span className="tw-text-sm tw-text-gray-700">
                            Update all current plate renewals to use this payment method
                        </span>
                    </label>
                    <div className="tw-flex tw-justify-end">
                        <button
                            onClick={handleAddPaymentMethod}
                            className="tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white tw-text-sm tw-font-medium tw-px-6 tw-py-2.5 tw-rounded-lg tw-border-0 tw-cursor-pointer tw-transition-colors"
                        >
                            Save Payment Method
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default PaymentMethods;
